import React, { useState } from 'react';
import { Search, SlidersHorizontal, X, Calendar, BookOpen } from 'lucide-react';
import Header from './components/Header19';
import Footer from './components/Footer19';
import ProductCard from './components/ProductCard';

const Shop = () => {
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [sortBy, setSortBy] = useState('featured');
    const [showFilters, setShowFilters] = useState(false);
    const [priceRange, setPriceRange] = useState([0, 10000]);

    const categories = ['All', 'Fashion', 'Lifestyle', 'Culture', 'New Arrivals'];
    const sortOptions = [
        { value: 'featured', label: 'Editor\'s Choice' },
        { value: 'price-low', label: 'Price: Low to High' },
        { value: 'price-high', label: 'Price: High to Low' },
        { value: 'newest', label: 'Latest First' },
    ];

    const products = [
        {
            id: 1,
            name: 'Sustainable Silk Scarf',
            category: 'Fashion',
            price: 2999,
            originalPrice: 3999,
            image: 'https://images.unsplash.com/photo-1520903920243-00d872a2d1c9?w=600',
            featured: true,
        },
        {
            id: 2,
            name: 'Artisan Leather Journal',
            category: 'Lifestyle',
            price: 1999,
            image: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=600',
        },
        {
            id: 3,
            name: 'Heritage Wool Coat',
            category: 'Fashion',
            price: 8999,
            originalPrice: 11999,
            image: 'https://images.unsplash.com/photo-1539533018447-63fcce2678e3?w=600',
            featured: true,
        },
        {
            id: 4,
            name: 'Handcrafted Ceramic Mug',
            category: 'Lifestyle',
            price: 899,
            image: 'https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?w=600',
        },
        {
            id: 5,
            name: 'Contemporary Art Print',
            category: 'Culture',
            price: 1499,
            image: 'https://images.unsplash.com/photo-1513519245088-0e12902e5a38?w=600',
        },
        {
            id: 6,
            name: 'Organic Cotton Tee',
            category: 'Fashion',
            price: 1299,
            image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=600',
            featured: true,
        },
        {
            id: 7,
            name: 'Vintage Inspired Dress',
            category: 'Fashion',
            price: 4999,
            image: 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=600',
        },
        {
            id: 8,
            name: 'Ethical Leather Boots',
            category: 'Fashion',
            price: 6999,
            originalPrice: 8999,
            image: 'https://images.unsplash.com/photo-1638247025967-b4e38f787b76?w=600',
        },
        {
            id: 9,
            name: 'Minimalist Watch',
            category: 'New Arrivals',
            price: 5499,
            image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600',
            featured: true,
        },
        {
            id: 10,
            name: 'Sustainable Canvas Bag',
            category: 'Lifestyle',
            price: 2299,
            image: 'https://images.unsplash.com/photo-1590874103328-eac38a683ce7?w=600',
        },
        {
            id: 11,
            name: 'Designer Sunglasses',
            category: 'Fashion',
            price: 3999,
            image: 'https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=600',
        },
        {
            id: 12,
            name: 'Handwoven Throw',
            category: 'Lifestyle',
            price: 4499,
            image: 'https://images.unsplash.com/photo-1584100936595-c0654b55a2e2?w=600',
        },
    ];

    const filteredProducts = selectedCategory === 'all'
        ? products
        : products.filter(p => p.category.toLowerCase() === selectedCategory.toLowerCase());

    return (
        <div className="min-h-screen bg-white font-serif">
            <Header cartCount={3} />

            <div className="container mx-auto px-4 lg:px-8 py-8 lg:py-12">
                {/* Editorial Header */}
                <div className="mb-12 lg:mb-16 text-center">
                    <div className="flex items-center justify-center gap-3 mb-6">
                        <Calendar className="text-emerald-600" size={20} />
                        <span className="text-sm text-emerald-600 tracking-widest font-medium">CURATED COLLECTION</span>
                    </div>
                    <h1 className="text-4xl lg:text-6xl font-bold text-emerald-800 mb-4">Editorial Shop</h1>
                    <div className="w-24 h-px bg-emerald-600 mx-auto mb-6"></div>
                    <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                        Discover pieces that define conscious fashion and timeless style
                    </p>
                </div>

                {/* Search and Filter Bar */}
                <div className="flex flex-col md:flex-row gap-4 mb-8 max-w-4xl mx-auto">
                    {/* Search */}
                    <div className="flex-1 relative">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-emerald-600" size={20} />
                        <input
                            type="text"
                            placeholder="Search editorial pieces..."
                            className="w-full pl-12 pr-4 py-3 border border-emerald-200 focus:border-emerald-600 focus:outline-none transition-colors"
                        />
                    </div>

                    {/* Sort */}
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="px-4 py-3 border border-emerald-200 focus:border-emerald-600 focus:outline-none transition-colors"
                    >
                        {sortOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>

                    {/* Filter Toggle */}
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="px-6 py-3 bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2 font-medium"
                    >
                        <SlidersHorizontal size={20} />
                        Filters
                    </button>
                </div>

                {/* Filters Panel */}
                {showFilters && (
                    <div className="bg-emerald-50 border border-emerald-200 p-6 mb-8 max-w-4xl mx-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="font-bold text-lg text-emerald-800 flex items-center gap-2">
                                <BookOpen size={20} />
                                Refine Your Selection
                            </h3>
                            <button onClick={() => setShowFilters(false)} className="text-emerald-600 hover:text-emerald-800 transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {/* Price Range */}
                            <div>
                                <h4 className="font-semibold mb-3 text-sm text-emerald-700 tracking-wide">PRICE RANGE</h4>
                                <div className="space-y-2">
                                    <input
                                        type="range"
                                        min="0"
                                        max="15000"
                                        value={priceRange[1]}
                                        onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                                        className="w-full accent-emerald-600"
                                    />
                                    <div className="flex justify-between text-sm text-emerald-700">
                                        <span>₹0</span>
                                        <span>₹{priceRange[1]}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Size */}
                            <div>
                                <h4 className="font-semibold mb-3 text-sm text-emerald-700 tracking-wide">SIZE</h4>
                                <div className="flex flex-wrap gap-2">
                                    {['XS', 'S', 'M', 'L', 'XL'].map(size => (
                                        <button
                                            key={size}
                                            className="px-4 py-2 border border-emerald-300 text-emerald-700 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-colors font-medium"
                                        >
                                            {size}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Collection Type */}
                            <div>
                                <h4 className="font-semibold mb-3 text-sm text-emerald-700 tracking-wide">COLLECTION</h4>
                                <div className="space-y-2">
                                    {['Sustainable', 'Heritage', 'Contemporary', 'Artisan'].map(type => (
                                        <label key={type} className="flex items-center gap-2 cursor-pointer text-sm text-emerald-800 hover:text-emerald-600 transition-colors">
                                            <input type="checkbox" className="form-checkbox accent-emerald-600 border-emerald-300" />
                                            <span>{type}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Category Tabs */}
                <div className="flex flex-wrap justify-center gap-6 mb-12">
                    {categories.map(category => (
                        <button
                            key={category}
                            onClick={() => setSelectedCategory(category.toLowerCase())}
                            className={`text-sm tracking-wide transition-colors pb-2 border-b-2 ${
                                selectedCategory === category.toLowerCase()
                                    ? 'border-emerald-600 text-emerald-700 font-semibold'
                                    : 'border-transparent text-gray-600 hover:text-emerald-600 font-medium'
                            }`}
                        >
                            {category.toUpperCase()}
                        </button>
                    ))}
                </div>

                {/* Products Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-8">
                    {filteredProducts.map(product => (
                        <ProductCard key={product.id} product={product} />
                    ))}
                </div>

                {/* Load More */}
                <div className="text-center mt-12">
                    <button className="bg-emerald-600 text-white px-8 py-3 hover:bg-emerald-700 transition-colors font-medium tracking-wide">
                        Load More Editorial Pieces
                    </button>
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default Shop;
